import {Component, ElementRef, EventEmitter, OnDestroy, Output, ViewChild} from '@angular/core';
import {ModalsService} from "../../services/modals.service";
import {Subscription} from "rxjs";
import {AuthService} from "../../services/auth.service";
import {environment} from "../../../environments/environment";

@Component({
  selector: 'app-auth-modal',
  templateUrl: './auth-modal.component.html',
  styleUrls: ['./auth-modal.component.css']
})
export class AuthModalComponent implements OnDestroy {
  @Output() successAuthCallback: EventEmitter<void>;
  private _codeInputsBlock: ElementRef;

  msisdn: string;
  msisdnError: string;

  smsRequestInProgress: boolean;
  smsSent: boolean;
  smsTimout: number;
  smsTimoutId: ReturnType<typeof setTimeout> | null;

  codeNumbers: string[];
  code: string;
  codeError: string;

  loginRequestInProgress: boolean;
  loginRequestSubscription: Subscription;
  appUrl: string = environment.appUrl;

  constructor(private readonly modalsService: ModalsService,
              private readonly authService: AuthService) {
    this.successAuthCallback = new EventEmitter<void>();

    this.msisdn = '';
    this.msisdnError = null;

    this.smsRequestInProgress = false;
    this.smsSent = false;
    this.smsTimout = 0;
    this.smsTimoutId = null;

    this.codeNumbers = ['', '', '', ''];
    this.code = '';
    this.codeError = null;

    this.loginRequestInProgress = false;
    this.loginRequestSubscription = null;
  }

  @ViewChild('codeInputsBlock') set codeInputsBlock(value: ElementRef) {
    this._codeInputsBlock = value;

    if (this._codeInputsBlock) {
      this._codeInputsBlock.nativeElement.firstChild.focus();
    }
  }

  close(): void {
    this.modalsService.hideModal();
  }

  refreshStates() {
    this.codeError = null;
    this.code = '';
    this.codeNumbers = ['', '', '', ''];
  }

  sendSms(): void {
    let msisdnValid = this.validateMsisdn();

    if (!this.smsRequestInProgress && msisdnValid) {
      this.smsRequestInProgress = true;
      this.refreshStates();
      this.authService.sendPassword(this.msisdn)
        .subscribe(code => {
          if (code === 200) {
            this.smsSentSuccessfully();
          } else if (code === 406) {
            this.msisdnError = 'auth-modal.wrong-operator';
          } else if (code === 429) {
            this.msisdnError = 'auth-modal.5-min-lock';
          } else if (code === 423) {
            this.msisdnError = 'auth-modal.24-hours-lock';
          } else {
            this.msisdnError = 'auth-modal.unknown-sms-error';
          }
          this.smsRequestInProgress = false;
        })
    }
  }

  login(): void {
    this.code = '';
    this.codeNumbers.forEach(value => this.code += value);

    let codeValid = this.validateCode();

    if (!this.loginRequestInProgress && codeValid) {
      this.loginRequestInProgress = true;

      this.authService.login(this.msisdn, this.code)
        .subscribe(code => {
          if (code === 200) {
            this.successAuth();
          } else if (code === 403) {
            this.codeError = 'auth-modal.wrong-code';
          } else if (code === 406) {
            this.codeError = 'auth-modal.wrong-operator';
          } else if (code === 429) {
            this.codeError = 'auth-modal.5-min-lock';
          } else if (code === 423) {
            this.codeError = 'auth-modal.24-hours-lock';
          } else {
            this.codeError = 'auth-modal.unknown-sms-error';
          }
          this.loginRequestInProgress = false;
        });
    }
  }

  codeChanged(): void {
    if (this.codeError) {
      this.codeError = null;
    }
    if (this._codeInputsBlock) {
      let index = this.getActiveCodeField();

      if (index > 0 && this.codeNumbers[index]?.length == 0) {
        index--;
      } else if (index < 3 && this.codeNumbers[index]?.length > 0) {
        index++;
      }
      this.setFocused(index);
    }

    this.loginIfCodeEntered();
  }

  moveLeft(): void {
    let index = this.getActiveCodeField();
    if (index > 0) {
      index--;
      this.setFocused(index);
    }
  }

  moveRight(): void {
    let index = this.getActiveCodeField();
    if (index < 3) {
      index++;
      this.setFocused(index);
    }
  }

  keydown(event: KeyboardEvent): void {
    if (event.key == 'Backspace') {
      let index = this.getActiveCodeField();

      if (this.codeNumbers[index]?.length == 0 && index > 0) {
        index--;
        this.setFocused(index);
      }
    } else if (/[0-9]/.test(event.key)) {
      let index = this.getActiveCodeField();

      if (this.codeNumbers[index]?.length > 0 && index < 3) {
        index++;
        this.setFocused(index);
      }
    }
  }

  pasteCode(event: ClipboardEvent): void {
    let clipboardData = event.clipboardData;
    let pastedText = clipboardData?.getData('text');
    let length = pastedText.length;

    if (pastedText && length > 0) {
      for (let i = 0; i < 4; i++) {
        this.codeNumbers[i] = i < length ? pastedText[i] : '';
      }

      this.setFocused(length < 4 ? length : 3);
      this.loginIfCodeEntered();
    }
    event.preventDefault();
  }

  successAuth(): void {
    this.successAuthCallback.emit();
    this.modalsService.hideModal();
  }

  msisdnChanged(): void {
    const normalized = this.normalizeMsisdn(this.msisdn);

    if (normalized !== (this.msisdn ?? '')) {
      this.msisdn = normalized;
    }

    if (this.msisdnError) this.msisdnError = null;
  }

  private normalizeMsisdn(raw: string): string {
    let digits = (raw ?? '').replace(/\D/g, '');
    if (digits.length === 0) return '';

    if (digits.startsWith('8')) {
      digits = '7' + digits.slice(1);
    } else if (!digits.startsWith('7')) {
      digits = digits.length === 1 ? ('7' + digits) : ('7' + digits.slice(1));
    }

    if (digits.length > 11) digits = digits.slice(0, 11);

    return digits;
  }


  onPaste(event: ClipboardEvent) {
    const pastedData = event.clipboardData?.getData('text');
    if (pastedData) {
      let formattedMsisdn = pastedData.replace(/\D/g, '');
      if (formattedMsisdn.length <= 10) formattedMsisdn = '7' + formattedMsisdn;
      this.msisdn = formattedMsisdn;
      this.msisdnChanged();
      event.preventDefault();
    }
  }

  onBackspace(event: Event): void {
    const e = event as KeyboardEvent;
    const input = e.target as HTMLInputElement;

    const caret = input.selectionStart ?? 0;
    if ((input.selectionEnd ?? 0) > caret) return;

    const viewValue = input.value ?? '';
    const beforeCaret = viewValue.slice(0, caret);
    const digitsBefore = (beforeCaret.match(/\d/g) || []).length;

    if (digitsBefore <= 0) return;

    const digits = this.normalizeMsisdn(this.msisdn ?? '');
    const idxToRemove = digitsBefore - 1;

    if (idxToRemove === 0) {
      e.preventDefault();
      return;
    }

    const newDigitsRaw = digits.slice(0, idxToRemove) + digits.slice(idxToRemove + 1);
    this.msisdn = this.normalizeMsisdn(newDigitsRaw);
    if (this.msisdnError) this.msisdnError = null;

    e.preventDefault();
    setTimeout(() => {
      input.dispatchEvent(new Event('input', { bubbles: true }));

      const updatedView = input.value ?? '';
      const targetDigitsBefore = idxToRemove;

      let pos = 0;
      let seen = 0;
      while (pos < updatedView.length && seen < targetDigitsBefore) {
        if (/\d/.test(updatedView[pos])) seen++;
        pos++;
      }
      input.setSelectionRange(pos, pos);
    });
  }

  backToMsisdn(): void {
    if (this.loginRequestSubscription) {
      this.loginRequestSubscription.unsubscribe();
      this.loginRequestSubscription = null;
    }
    this.codeNumbers = ['', '', '', ''];
    this.code = '';
    this.codeError = null;
    this.loginRequestInProgress = false;
    this.smsSent = false;
  }

  getTimeToSms(): string {
    let minutes = Math.trunc(this.smsTimout / 60);
    let seconds = this.smsTimout % 60;
    return minutes + ':' + (seconds > 9 ? '' : '0') + seconds;
  }

  private validateMsisdn(): boolean {
    if (!this.msisdn || this.msisdn.length != 11) {
      this.msisdnError = 'auth-modal.fill-field';
      return false;
    } else {
      this.msisdnError = null;
      return true;
    }
  }

  private validateCode(): boolean {
    if (!this.code || this.code.length != 4) {
      this.codeError = 'auth-modal.fill-field';
      return false;
    } else {
      this.codeError = null;
      return true;
    }
  }

  private startSmsTimout(): void {
    if (this.smsTimout > 0) {
      this.smsTimoutId = setTimeout(() => {
        this.smsTimout--;
        this.startSmsTimout();
      }, 1000);
    }
  }

  private stopSmsTimout(): void {
    if (this.smsTimout) {
      clearTimeout(this.smsTimoutId);
      this.smsTimoutId = null;
    }
  }

  private smsSentSuccessfully(): void {
    this.smsSent = true;
    this.stopSmsTimout();
    this.smsTimout = 60;
    this.startSmsTimout();
    if (this._codeInputsBlock) {
      this._codeInputsBlock.nativeElement.firstChild.focus();
    }
  }

  private getActiveCodeField(): number {
    if (this._codeInputsBlock) {
      let children = this._codeInputsBlock.nativeElement.children;

      for (let i = 0; i < children.length; i++) {
        if (children.item(i) == document.activeElement) {
          return i;
        }
      }
    }
    return 0;
  }

  private setFocused(index: number): void {
    if (this._codeInputsBlock) {
      let item = this._codeInputsBlock.nativeElement.children.item(index);
      item.focus();
      item.setSelectionRange(1, 1);
    }
  }

  private loginIfCodeEntered(): void {
    let codeEntered = true;
    this.codeNumbers.forEach(value => codeEntered = codeEntered && value.length > 0);

    if (codeEntered) {
      this.login();
    }
  }

  ngOnDestroy(): void {
    this.stopSmsTimout();
  }
}
